import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Inject,
  Input,
  OnInit,
  Optional,
  Output,
  ViewChild,
} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';

import { MojoEditorComponent } from 'mojo-editor';
import { Feedback, FeedbackType, Resetable, ThumbValue } from '@shared/models';

import { FeedbackMenuType, FeedbackTag } from './feedback-menu.data';

@Component({
  selector: 'mojo-feedback-menu-content',
  templateUrl: './feedback-menu-content.component.html',
  styleUrls: ['./feedback-menu-content.component.scss'],
})
export class FeedbackMenuContentComponent implements OnInit, Resetable {
  @Input() menuType: FeedbackMenuType = FeedbackMenuType.TAGS;
  @Input() feedbackModel: Feedback;
  @Input() resultType: FeedbackType = this.data?.resultType;
  @Input() title: string = this.data?.title;
  @Input() thumbIcon: ThumbValue = this.data?.thumbIcon;
  @Input() reasonTags: FeedbackTag[] = this.data ? this.data.reasonTags : [];
  @Input() raisedThumbButton = this.data ? this.data.raisedThumbButton : false;
  @Output() feedbackSent: EventEmitter<Feedback[]> = new EventEmitter<Feedback[]>();

  @ViewChild(MojoEditorComponent) editor: MojoEditorComponent;

  TAGS_MENU = FeedbackMenuType.TAGS;
  TEXT_AREA_MENU = FeedbackMenuType.TEXT_AREA;
  TEXT_AREA_TAGS = FeedbackMenuType.TEXT_AREA_TAGS;

  note: UntypedFormControl = new UntypedFormControl('', [
    Validators.required,
    Validators.minLength(1),
    Validators.maxLength(3000),
  ]);
  form = new UntypedFormGroup({
    note: this.note,
  });

  private readonly isBatchOperation = !!this.data;

  constructor(
    @Optional() @Inject(MAT_DIALOG_DATA) public data: FeedbackMenuBatchOperationModalData,
    @Optional() public dialogRef: MatDialogRef<FeedbackMenuContentComponent>,
  ) {}

  ngOnInit(): void {
    if (this.isBatchOperation) {
      this.positionBatchModal();
    }
    this.reset();
  }

  @HostListener('window:resize')
  onWindowResize() {
    if (this.isBatchOperation) {
      this.positionBatchModal();
    }
  }

  private positionBatchModal() {
    const remToPxMultiplier = 16;
    const currentHeightPx = this.data.currentDialogHeightRem * remToPxMultiplier;
    const rect: DOMRect = this.data.positionRelativeTo.nativeElement.getBoundingClientRect();
    const spaceBetweenToolbarAndDialogPx = 13;
    const top = `${rect.top - currentHeightPx - spaceBetweenToolbarAndDialogPx}px`;
    const left = `${rect.left}px`;
    const matDialogConfig = new MatDialogConfig();
    matDialogConfig.position = { top, left };
    this.dialogRef.updatePosition(matDialogConfig.position);
  }

  reset(): void {
    this.resetTags();
    this.resetMenu();
  }

  private resetMenu() {
    if (
      (this.menuType === FeedbackMenuType.TEXT_AREA ||
        this.menuType === FeedbackMenuType.TEXT_AREA_TAGS) &&
      !!this.editor
    ) {
      this.editor.reset();
    }
  }

  private resetTags() {
    if (
      this.menuType === FeedbackMenuType.TAGS ||
      this.menuType === FeedbackMenuType.TEXT_AREA_TAGS
    ) {
      this.selectedReasons().forEach((reason) => (reason.selected = false));
    }
  }

  onSendMatchReasons() {
    let feedback;
    switch (this.menuType) {
      case FeedbackMenuType.TAGS:
        feedback = this.getTagsFeedback();
        break;
      case FeedbackMenuType.TEXT_AREA:
        feedback = this.getAreaFeedback();
        break;
      case FeedbackMenuType.TEXT_AREA_TAGS:
        feedback = this.getTagsAreaFeedback();
        break;
    }
    this.feedbackSent.emit(feedback);
    // tslint:disable-next-line:no-unused-expression
    this.dialogRef && this.dialogRef.close();
  }

  private getAreaFeedback() {
    return [
      {
        ...this.feedbackModel,
        type: this.resultType,
        value: this.note.value,
      } as Feedback,
    ];
  }

  private getTagsFeedback() {
    if (this.isBatchOperation) {
      return this.getFeedbackForBatchOperation();
    } else {
      return this.selectedReasons().map(
        (reason: FeedbackTag) =>
          ({
            ...this.feedbackModel,
            type: this.resultType,
            value: reason.text,
          } as Feedback),
      );
    }
  }

  private getTagsAreaFeedback() {
    return [...this.getTagsFeedback(), ...this.getAreaFeedback()];
  }

  private getFeedbackForBatchOperation() {
    const result: Feedback[] = [];
    this.selectedReasons().forEach((reason: FeedbackTag) => {
      const feedbacks: Feedback[] = this.data.feedbackModels.map(
        (feedbackModel) =>
          ({
            ...feedbackModel,
            type: this.resultType,
            value: reason.text,
          } as Feedback),
      );
      result.push(
        ...feedbacks.map(
          (feedback) =>
            ({
              ...feedback,
              type: this.resultType,
              value: reason.text,
            } as Feedback),
        ),
      );
    });
    return result;
  }

  selectedReasons(): FeedbackTag[] {
    return this.reasonTags.filter((r) => r.selected);
  }

  reasonSetSelected(selectedChipIndex: number, valueToSet: boolean): void {
    this.reasonTags[selectedChipIndex].selected = valueToSet;
  }

  getButtonText(): string {
    const isBatchOperation = !!this.data;
    const isAdvanceOperation: boolean = this.thumbIcon === ThumbValue.UP;
    return isBatchOperation && isAdvanceOperation
      ? 'ADVANCE'
      : isAdvanceOperation
      ? 'SEND'
      : 'REJECT';
  }

  validForm(): boolean {
    switch (this.menuType) {
      case FeedbackMenuType.TAGS:
        return this.selectedReasons().length === 0;
      case FeedbackMenuType.TEXT_AREA:
        return !this.form?.valid;
      case FeedbackMenuType.TEXT_AREA_TAGS:
        return this.selectedReasons().length === 0 && !this.form?.valid;
    }
  }
}

export interface FeedbackMenuBatchOperationModalData {
  feedbackModels: Feedback[];
  title: string;
  thumbIcon: ThumbValue;
  reasonTags: FeedbackTag[];
  resultType: FeedbackType;
  raisedThumbButton: boolean;
  positionRelativeTo: ElementRef;
  currentDialogHeightRem: number;
}
